import type { SCAPIShopperProductsSchemas } from '@/types';

export type MultipackOption = {
    productId: string;
    /** Pack count shown on the tile (1 for the unit SKU, N for a multipack bundle). */
    packQty: number;
    price?: number;
    href: string;
    orderable: boolean;
    selected: boolean;
    /**
     * Product used for €/um weight attrs on the tile.
     * Always the unit (base) product — pack tiles reuse it with their own packQty/price.
     */
    product: SCAPIShopperProductsSchemas['Product'];
};

type Product = SCAPIShopperProductsSchemas['Product'];

function toProductHref(url: unknown): string {
    return typeof url === 'string' && url.length > 0 ? url : '#';
}

/**
 * Frontend multipack signal: hook injected sibling packs on base or bundle PDP.
 * Do not use the catalog heuristic (`type.bundle` + 1 child + qty > 1) in UI.
 */
export function hasMultiPackBundles(product: Product | null | undefined): boolean {
    return Boolean(product?.c_multiPackBundles && product.c_multiPackBundles.length > 0);
}

/**
 * Editorial content source: always the unit/base product.
 * On a multipack bundle PDP / cart line that is the embedded child; on the base that is self.
 */
export function getMultipackContentProduct<
    T extends {
        type?: { bundle?: boolean };
        bundledProducts?: Array<{ product?: T; quantity?: number }> | null;
    },
>(product: T): T {
    if (product.type?.bundle && isMultipackBundledProductsShape(product.bundledProducts)) {
        return product.bundledProducts?.[0]?.product ?? product;
    }
    return product;
}

/**
 * Multipack-shaped bundle payload: exactly one distinct child with quantity &gt; 1.
 * Guards tile/PDP logic so assortment bundles (multiple children) are ignored.
 */
export function isMultipackBundledProductsShape(
    bundledProducts: Array<{ quantity?: number }> | null | undefined
): boolean {
    if (!bundledProducts || bundledProducts.length !== 1) {
        return false;
    }
    const qty = bundledProducts[0]?.quantity;
    return typeof qty === 'number' && qty > 1;
}

/**
 * Pack quantity when `bundledProducts` is multipack-shaped (1 child × N); otherwise undefined.
 */
function packQtyFromMultipackBundledProducts(
    bundledProducts: Array<{ quantity?: number }> | null | undefined
): number | undefined {
    if (!isMultipackBundledProductsShape(bundledProducts) || !bundledProducts) {
        return undefined;
    }
    const qty = bundledProducts[0]?.quantity;
    return typeof qty === 'number' && qty > 1 ? qty : undefined;
}

/**
 * Pack quantity for a multipack SKU (PDP / cart line).
 * Uses sole `bundledProducts[0].quantity` when `c_multiPackBundles` is present
 * and the bundle is multipack-shaped (1 child × N); otherwise 1.
 */
export function getMultipackPackQty(
    product:
        | Product
        | {
              c_multiPackBundles?: Product['c_multiPackBundles'];
              type?: { bundle?: boolean };
              bundledProducts?: Array<{ quantity?: number; product?: Product }> | null;
          }
        | null
        | undefined
): number {
    if (!product || !hasMultiPackBundles(product as Product)) {
        return 1;
    }
    return packQtyFromMultipackBundledProducts(product.bundledProducts) ?? 1;
}

/**
 * Pack quantity for a tile/search hit.
 * Uses `bundledProducts[0].quantity` only when there is exactly one child with qty &gt; 1
 * (Product→hit conversion / enriched hits). Falls back to slim `c_multipackPackQty`
 * from the shopper-search hook (raw PLP hits do not include bundledProducts).
 */
export function getSearchHitMultipackPackQty(
    hit:
        | {
              c_multipackPackQty?: number;
              bundledProducts?: Array<{ quantity?: number }> | null;
          }
        | null
        | undefined
): number {
    const fromBundled = packQtyFromMultipackBundledProducts(hit?.bundledProducts);
    if (fromBundled != null) {
        return fromBundled;
    }
    const qty = hit?.c_multipackPackQty;
    return typeof qty === 'number' && qty > 1 ? qty : 1;
}

/**
 * True when a search/tile hit is a multipack-shaped bundle (1 child × N).
 * Used to ATC the parent bundle id instead of navigating to PDP or adding the unit child.
 */
export function isMultipackSearchHit(
    hit:
        | {
              c_multipackPackQty?: number;
              bundledProducts?: Array<{ quantity?: number }> | null;
              productId?: string;
              c_multiPackBundles?: Array<{ id?: string; packQty?: number }> | null;
          }
        | null
        | undefined
): boolean {
    if (!hit) {
        return false;
    }
    if (getSearchHitMultipackPackQty(hit) > 1) {
        return true;
    }
    // PLP hit that is itself one of the injected sibling packs (qty on matching entry).
    const self = hit.c_multiPackBundles?.find((entry) => entry.id === hit.productId);
    return typeof self?.packQty === 'number' && self.packQty > 1;
}

/**
 * PDP / PLP weight line: `7 x 330g` when multipack pack qty &gt; 1, else the eti weight as-is.
 */
export function formatMultipackWeightText(weightText: string | undefined, packQty: number): string | undefined {
    if (!weightText) {
        return weightText;
    }
    return packQty > 1 ? `${packQty} x ${weightText}` : weightText;
}

/**
 * Builds pack tabs: unit (qty 1) + entries from `c_multiPackBundles`, sorted by packQty.
 * Selected = current `product.id`. Returns null when the hook did not inject siblings.
 */
export function buildMultipackOptions(product: Product): MultipackOption[] | null {
    if (!hasMultiPackBundles(product)) {
        return null;
    }

    const contentProduct = getMultipackContentProduct(product);
    const unitId = contentProduct.id;
    if (!unitId) {
        return null;
    }

    const options: MultipackOption[] = [
        {
            productId: unitId,
            packQty: 1,
            price: contentProduct.price,
            href: toProductHref((contentProduct as { c_url?: unknown }).c_url),
            orderable: contentProduct.inventory?.orderable !== false,
            selected: product.id === unitId,
            product: contentProduct,
        },
    ];

    for (const entry of product.c_multiPackBundles ?? []) {
        if (!entry?.id || !(entry.packQty > 1)) {
            continue;
        }
        // Avoid duplicating the unit row if a bad catalog value slips in.
        if (entry.id === unitId) {
            continue;
        }
        options.push({
            productId: entry.id,
            packQty: entry.packQty,
            price: entry.price,
            href: toProductHref(entry.c_url),
            orderable: entry.orderable !== false,
            selected: product.id === entry.id,
            product: contentProduct,
        });
    }

    return options.sort((a, b) => a.packQty - b.packQty);
}
